import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    ArticleDto,
    BatchDto,
    ClientDto,
    GoodsBatchItemDto,
    GoodsDto,
    GoodsStatus,
    IGoodsDto,
    SiteDto,
} from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';
import { updateReduxSite } from './siteActions';

export const createNewEmptyGoodsBatchEntity = createAction('goods/createNewEmptyGoodsBatchEntity');
export const removeGoodsBatchEntity = createAction<number>('goods/removeGoodsBatchEntity');
export const clearGoods = createAction('goods/clearGoods');

export const updateGoodsArticle = createAction<{ index: number; article?: ArticleDto }>('goods/updateGoodsArticle');
export const updateGoodsStatus = createAction<{ index: number; goodsStatus?: GoodsStatus }>('goods/updateGoodsStatus');
export const updateGoodsQuantity = createAction<{ index: number; quantity?: number }>('goods/updateGoodsQuantity');
export const updateGoodsSiteId = createAction<{ index: number; siteId?: number }>('goods/updateGoodsSiteId');
export const updateGoodsSite = createAction<{ index: number; site?: SiteDto }>('goods/updateGoodsSite');

export const updateBatchPrice = createAction<{ index: number; batchPrice?: number }>('goods/updateBatchPrice');
export const updateBatchUniqueId = createAction<{ index: number; uniqueBatchId?: string }>('goods/updateBatchUniqueId');
export const updateBatchClient = createAction<{ index: number; batchClient?: ClientDto }>('goods/updateBatchClient');
export const updateBatchArrivalTime = createAction<{ index: number; batchArrivalTime?: Date }>(
    'goods/updateBatchArrivalTime',
);

export const updateBatchForGood = createAction<BatchDto>('goods/updateBatchForGood');

export const createNewGoods = createAction<IGoodsDto>('goods/createNewGoods');

export const addOrUpdateReduxGoods = createAction<IGoodsDto>('goods/addOrUpdateReduxGoods');
export const addOrUpdateMultipleReduxGoods = createAction<IGoodsDto[]>('goods/addOrUpdateMultipleReduxGoods');
export const removeGoodsFromRedux = createAction<number>('goods/removeGoodsFromRedux');

export const saveGoods = createAsyncThunk<GoodsDto[], number | undefined>(
    'goods/saveGoods',
    async (payload: number | undefined, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const goodsState = rootState.goodsForm;

        const goods = goodsState.goodBatchEntities;

        const siteId = payload;

        myAssert(() => goods !== null);

        if (goods === null || goods.length <= 0 || !siteId) {
            throw new Error();
        }

        let savedGoods: GoodsDto[] = [];

        const savedGoodsPromises: Promise<GoodsDto>[] = [];

        goods.map(async (good) => {
            const goodBatch = new GoodsBatchItemDto();
            const newGood = good.goods;
            const newBatch = good.batch;

            goodBatch.articleId = newGood.article?.id;
            goodBatch.batchArrivalTime = newBatch.batchArrivalTime;
            goodBatch.supplierId = newBatch.supplier?.id;
            goodBatch.goodsStatus = GoodsStatus.InStock;
            goodBatch.quantity = newGood.quantity;
            goodBatch.siteId = siteId;
            goodBatch.uniqueBatchId = newBatch.uniqueBatchId;
            goodBatch.purchasePricePerArticle = newBatch.purchasePricePerArticle;

            const addGoodsPromise = WarehouseClient.addNewGoods(goodBatch);

            savedGoodsPromises.push(addGoodsPromise);
        });

        savedGoods = await Promise.all(savedGoodsPromises).then((res) => {
            return res;
        });

        return savedGoods;
    },
    {
        condition: (payload: number | undefined, thunkAPI: { getState: any }) => {
            const goodsState = (thunkAPI.getState() as RootState).goodsForm;

            return goodsState.goodBatchEntities !== null && !!payload;
        },
    },
);

export const importAllGoods = createAsyncThunk<GoodsDto[]>(
    'goods/importAllGoods',
    async (payload, thunkAPI) => {
        return WarehouseClient.getAllGoods();
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const goodsState = (thunkAPI.getState() as RootState).goods;

            return !goodsState.goodsFetchActive;
        },
    },
);

export const importGoods = createAsyncThunk<GoodsDto, number>('goods/importGoods', async (payload, thunkAPI) => {
    return WarehouseClient.getGoodsById(payload);
});

export const sellGoods = createAsyncThunk<GoodsDto[], void>(
    'goods/sellGoods',
    async (payload, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const goodsManipulationState = rootState.goodsStatusManipulation;

        const soldGoodsItem = goodsManipulationState.goodsManipulationItem;

        myAssert(() => soldGoodsItem !== null);

        if (soldGoodsItem === null) {
            throw new Error();
        }

        return WarehouseClient.sellGoodsForSite(soldGoodsItem).then((res) => {
            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(res));
            if (!!soldGoodsItem.goodsOriginSiteId) {
                thunkAPI.dispatch(updateReduxSite(soldGoodsItem.goodsOriginSiteId));
            }
            return res;
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const goodsManipulationState = (thunkAPI.getState() as RootState).goodsStatusManipulation;

            return goodsManipulationState.goodsManipulationItem !== null;
        },
    },
);

export const useGoods = createAsyncThunk<GoodsDto[], void>(
    'goods/useGoods',
    async (payload, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const goodsManipulationState = rootState.goodsStatusManipulation;

        const usedGoodsItem = goodsManipulationState.goodsManipulationItem;

        myAssert(() => usedGoodsItem !== null);

        if (usedGoodsItem === null) {
            throw new Error();
        }

        return WarehouseClient.useGoodsForSite(usedGoodsItem).then((res) => {
            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(res));
            if (!!usedGoodsItem.goodsOriginSiteId) {
                thunkAPI.dispatch(updateReduxSite(usedGoodsItem.goodsOriginSiteId));
            }
            return res;
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const goodsManipulationState = (thunkAPI.getState() as RootState).goodsStatusManipulation;

            return goodsManipulationState.goodsManipulationItem !== null;
        },
    },
);

export const writeOffGoods = createAsyncThunk<GoodsDto[], void>(
    'goods/writeOffGoods',
    async (payload, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const goodsManipulationState = rootState.goodsStatusManipulation;

        const writtenOffGoodsItem = goodsManipulationState.goodsManipulationItem;

        myAssert(() => writtenOffGoodsItem !== null);

        if (writtenOffGoodsItem === null) {
            throw new Error();
        }

        return WarehouseClient.writeOffGoodsForSite(writtenOffGoodsItem).then((res) => {
            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(res));
            if (!!writtenOffGoodsItem.goodsOriginSiteId) {
                thunkAPI.dispatch(updateReduxSite(writtenOffGoodsItem.goodsOriginSiteId));
            }
            return res;
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const goodsManipulationState = (thunkAPI.getState() as RootState).goodsStatusManipulation;

            return goodsManipulationState.goodsManipulationItem !== null;
        },
    },
);

export const writeOffGoodsForTransport = createAsyncThunk<GoodsDto[], void>(
    'goods/writeOffGoodsForTransport',
    async (payload, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const goodsManipulationState = rootState.goodsStatusManipulation;

        const writtenOffGoodsItem = goodsManipulationState.goodsManipulationItem;

        myAssert(() => writtenOffGoodsItem !== null);

        if (writtenOffGoodsItem === null) {
            throw new Error();
        }

        return WarehouseClient.writeOffGoodsForOrderInTransport(writtenOffGoodsItem).then((res) => {
            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(res));
            return res;
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const goodsManipulationState = (thunkAPI.getState() as RootState).goodsStatusManipulation;

            return goodsManipulationState.goodsManipulationItem !== null;
        },
    },
);

export const createNewSellGoodsManipulationItem = createAction<{ siteId: number; articleId: number }>(
    'goods/createNewSellGoodsManipulationItem',
);
export const createNewUseGoodsManipulationItem = createAction<{ siteId: number; articleId: number }>(
    'goods/createNewUseGoodsManipulationItem',
);
export const createNewWriteOffGoodsManipulationItem = createAction<{ siteId: number; articleId: number }>(
    'goods/createNewWriteOffGoodsManipulationItem',
);
export const createNewWriteOffInTransportGoodsManipulationItem = createAction<{ orderId: number; articleId: number }>(
    'goods/createNewWriteOffInTransportGoodsManipulationItem',
);
export const clearGoodsManipulationItem = createAction('goods/clearGoodsManipulationItem');
export const setGoodsManipulationItemArticleId = createAction<number>('goods/setGoodsManipulationItemArticleId');
export const setGoodsManipulationItemOrderId = createAction<number>('goods/setGoodsManipulationItemOrderId');
export const setGoodsManipulationItemSiteId = createAction<number>('goods/setGoodsManipulationItemSiteId');
export const setGoodsManipulationItemExecutionTime = createAction<Date>('goods/setGoodsManipulationItemExecutionTime');
export const setGoodsManipulationItemQuantity = createAction<number>('goods/setGoodsManipulationItemQuantity');
export const setGoodsManipulationItemSituation = createAction<number>('goods/setGoodsManipulationItemSituation');
export const setGoodsManipulationItemReason = createAction<string>('goods/setGoodsManipulationItemReason');
export const setGoodsManipulationItemSellingPrice = createAction<number>('goods/setGoodsManipulationItemSellingPrice');
export const setGoodsManipulationItemClientId = createAction<number | undefined>(
    'goods/setGoodsManipulationItemClientId',
);
